import { useEffect, useRef, useState, useCallback } from "react";
import {
    Phone,
    ArrowUpRight,
    Check,
    Sparkles,
    ShieldCheck,
    Award,
    Clock,
    DollarSign,
    Wrench,
    Star,
    Leaf,
    AlertTriangle,
    Umbrella,
    Sun,
    MapPin,
    MoveHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header, AnnouncementBar, Footer } from "@/components/Layout";
import { SITE } from "@/lib/site";
import { useSeo, breadcrumbSchema, serviceSchema } from "@/lib/seo";

// 5 Star Construction roof-moss photos — AI-generated for site clarity (matched perspective).
const IMG_HERO = "/images/roof/gen-before.jpg";
const IMG_CLEAN = "/images/roof/gen-after.jpg";
const IMG_MOSS_DETAIL = "/images/roof/moss-tiles.jpg";

/**
 * Drag/tap to reveal — before-image is clipped horizontally to `pos`% and
 * layered on top of the after-image. Works with mouse + touch.
 */
function BeforeAfterSlider({ beforeSrc, beforeAlt, afterSrc, afterAlt }) {
    const [pos, setPos] = useState(50);
    const [dragging, setDragging] = useState(false);
    const wrapRef = useRef(null);

    const updateFromClientX = useCallback((clientX) => {
        const el = wrapRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const pct = ((clientX - rect.left) / rect.width) * 100;
        setPos(Math.max(0, Math.min(100, pct)));
    }, []);

    useEffect(() => {
        if (!dragging) return;
        const onMove = (e) => {
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            updateFromClientX(clientX);
        };
        const onUp = () => setDragging(false);
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        window.addEventListener("touchmove", onMove, { passive: true });
        window.addEventListener("touchend", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onUp);
        };
    }, [dragging, updateFromClientX]);

    return (
        <div
            ref={wrapRef}
            data-testid="roof-before-after"
            className="relative w-full h-full select-none cursor-ew-resize touch-none"
            onMouseDown={(e) => { setDragging(true); updateFromClientX(e.clientX); }}
            onTouchStart={(e) => { setDragging(true); updateFromClientX(e.touches[0].clientX); }}
        >
            {/* AFTER (base layer, full width) */}
            <img src={afterSrc} alt={afterAlt} loading="eager" draggable="false" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
            {/* BEFORE (clipped from the left) */}
            <div className="absolute inset-y-0 left-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
                <img
                    src={beforeSrc}
                    alt={beforeAlt}
                    loading="eager"
                    draggable="false"
                    className="absolute inset-y-0 left-0 h-full object-cover pointer-events-none"
                    style={{ width: wrapRef.current ? `${wrapRef.current.getBoundingClientRect().width}px` : "100%", maxWidth: "none" }}
                />
            </div>
            {/* Corner labels — positioned above the bottom info strip so the SAME DAY badge doesn't cover them */}
            <div className="absolute bottom-20 left-3 bg-[#E11D2A] text-white px-2.5 py-1 font-['Manrope'] text-[10px] font-black tracking-[0.22em] uppercase pointer-events-none" data-testid="roof-before-label">Before</div>
            <div className="absolute bottom-20 right-3 bg-[#FFD400] text-[#111111] px-2.5 py-1 font-['Manrope'] text-[10px] font-black tracking-[0.22em] uppercase pointer-events-none" data-testid="roof-after-label">After</div>
            {/* Divider line + handle */}
            <div className="absolute inset-y-0 pointer-events-none" style={{ left: `${pos}%`, transform: "translateX(-1px)" }}>
                <div className="w-0.5 h-full bg-[#F6F5F2] shadow-[0_0_0_1px_rgba(0,0,0,0.35)]" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 bg-[#FFD400] border-2 border-[#111111] rounded-full flex items-center justify-center shadow-block-sm">
                    <MoveHorizontal className="w-5 h-5 text-[#111111]" strokeWidth={2.5} />
                </div>
            </div>
        </div>
    );
}

const SERVICE_AREAS = [
    { region: "Southern Oregon", cities: "Medford · Ashland · Central Point · Phoenix · Talent · Eagle Point · White City · Jacksonville · Grants Pass" },
    { region: "Portland Metro, Oregon", cities: "Portland · Beaverton · Gresham · Tigard · Lake Oswego · Hillsboro · Milwaukie" },
    { region: "SW Washington", cities: "Vancouver · Battle Ground · Ridgefield · Camas · Washougal · Woodland · La Center" },
];

const WHY_US = [
    { i: DollarSign, t: "Most competitive prices", d: "We beat the national franchise quotes — same treatment, same warranty, fair local pricing." },
    { i: Clock, t: "Same-day transformations", d: "Show up in the morning, leave you with a clean roof by dinner. Most single-family homes done in one visit." },
    { i: ShieldCheck, t: "Licensed & Insured", d: "Oregon CCB #232477. Fully insured — no risk to your homeowner's policy if anything goes sideways." },
    { i: Award, t: "25 years on the tools", d: "PNW roofs, PNW moss, PNW weather. We know exactly how to treat what your roof is dealing with." },
];

const WHAT_WE_DO = [
    { i: Leaf, t: "Full moss removal", d: "Hand-scrape or soft-wash — never a power washer, which would tear up your shingle grit. We get the moss OFF, not just kill it in place." },
    { i: Sparkles, t: "Moss killer treatment", d: "EPA-registered zinc or sodium hypochlorite solution that neutralizes the roots and prevents regrowth for years." },
    { i: Umbrella, t: "Preventive zinc strips", d: "Optional galvanized zinc strips installed at the ridge — rainwater carries the zinc down the roof and keeps new moss from ever taking hold." },
    { i: Wrench, t: "Gutter clean-out", d: "All the moss and debris we pull off the roof ends up in your gutters — we clean those out too. No extra charge." },
    { i: AlertTriangle, t: "Damaged shingle check", d: "While we're up there, we inspect for lifted, cracked, or missing shingles and flag anything that needs a proper roofer's attention." },
    { i: Sun, t: "Skylight & vent cleaning", d: "We wipe down skylights, solartubes, and roof vents while we're there — bonus daylight for free." },
];

const PROCESS = [
    { t: "Free roof assessment", d: "We swing by, walk your roof (or drone-check it if it's too steep), and give you a real quote — usually same-day." },
    { t: "Protect the yard", d: "Tarps go down over any plants below the eaves. Kids' toys, patio furniture, garden beds — nothing gets touched by runoff." },
    { t: "Remove & treat", d: "Hand-scrape the heavy moss, soft-wash the rest, apply moss-killer solution to prevent regrowth. Zero pressure washing." },
    { t: "Clean up & haul off", d: "All the debris goes into our truck — nothing left in your driveway or side yard. Gutters cleaned as part of the job." },
];

const FAQ = [
    { q: "How much does roof de-mossing cost in Medford or Portland?", a: "Most single-family roofs run $350–$750 depending on square footage, pitch, and how heavy the moss is. We're consistently the most competitive priced local option — we'll match or beat any legit quote from a licensed competitor. Every quote is free." },
    { q: "Can you really finish in one day?", a: "Yes — 90% of the homes we treat are done in a single day, most in 3–5 hours. We show up early, work efficiently, and leave the roof looking new by dinner. Larger multi-story or steeply-pitched roofs may need a second visit." },
    { q: "Do you pressure-wash the roof?", a: "Never. Pressure washing strips the granules off asphalt shingles and voids most manufacturer warranties. We hand-scrape the heavy moss and soft-wash the rest with a low-pressure sprayer. That's the only right way to do it." },
    { q: "Will the moss come back?", a: "Not for years — especially if you opt for the zinc strip install. The moss-killer treatment kills the roots and spores; the zinc strips give you long-term prevention. Most customers go 4–7 years before needing another visit." },
    { q: "What areas do you service?", a: "Medford and all of Southern Oregon (Ashland, Grants Pass, Central Point, Phoenix, Talent, Eagle Point, Jacksonville, White City), Portland Metro (Portland, Beaverton, Gresham, Tigard, Lake Oswego, Hillsboro), and SW Washington (Vancouver, Battle Ground, Ridgefield, Camas, Washougal). Call for anywhere else — we cover a lot of ground." },
    { q: "Is roof moss actually harmful?", a: "Yes. Moss holds moisture against the shingles, which rots the mat underneath, lifts and curls the shingles, and eventually causes leaks. It also traps debris that clogs gutters. Every extra year you wait to treat it takes years off your roof's lifespan." },
    { q: "Are you insured?", a: "Fully. Oregon CCB #232477, general liability, and workers' comp on every crew member. You'll never be on the hook if something happens on your property." },
];

export default function RoofDemossing() {
    useSeo({
        title: "Roof De-Mossing & Moss Removal · Medford, Portland OR & Vancouver WA | 5 Star Construction",
        description: "Same-day roof de-mossing and moss removal — most competitive prices in Southern Oregon, Portland Metro, and SW Washington. Hand-scrape + soft-wash + moss-killer treatment. Licensed Oregon GC (CCB #232477). Free quotes — 541-292-9620.",
        path: "/roof-demossing",
        jsonLdKey: "roof-demossing",
        jsonLd: [
            serviceSchema({
                name: "Roof De-Mossing & Moss Removal",
                description: "Roof moss removal, moss-killer treatment, gutter cleanup, and preventive zinc-strip installation across Medford, Southern Oregon, Portland Metro, and SW Washington. Same-day service, most competitive prices, licensed and insured.",
                path: "/roof-demossing",
                serviceType: "Roof Cleaning & Moss Removal Contractor",
            }),
            breadcrumbSchema([
                { name: "Home", path: "/" },
                { name: "Services", path: "/#services" },
                { name: "Roof De-Mossing", path: "/roof-demossing" },
            ]),
            {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: FAQ.map((it) => ({
                    "@type": "Question",
                    name: it.q,
                    acceptedAnswer: { "@type": "Answer", text: it.a },
                })),
            },
        ],
    });

    return (
        <div className="bg-[#F6F5F2] min-h-screen">
            <AnnouncementBar />
            <Header />

            {/* Hero */}
            <section className="relative bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A] overflow-hidden">
                <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #FFD400 1px, transparent 0)", backgroundSize: "16px 16px" }} />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 mb-6 bg-[#FFD400] border border-[#FFD400] px-3 py-1.5">
                            <Leaf className="w-4 h-4 text-[#111111]" strokeWidth={2.5} />
                            <span className="font-['Manrope'] text-xs font-black tracking-[0.22em] uppercase text-[#111111]">Roof De-Mossing · OR + WA</span>
                        </div>
                        <h1 className="font-['Anton'] tracking-tight leading-[0.92] text-5xl sm:text-6xl lg:text-7xl uppercase">
                            Roof caked in moss?
                            <br />
                            <span className="text-[#FFD400]">Same-day fix.</span>
                        </h1>
                        <p className="mt-7 font-['Manrope'] text-lg md:text-xl leading-relaxed text-[#F6F5F2]/85 max-w-2xl">
                            The <strong className="text-[#F6F5F2]">most competitive prices</strong> in the PNW and <strong className="text-[#F6F5F2]">same-day transformations</strong> — most single-family homes done from moss-covered to spotless in one visit. Hand-scrape, soft-wash, moss-killer treatment, gutters cleaned. No pressure washing, no shortcuts.
                        </p>
                        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-['Manrope'] text-sm text-[#F6F5F2]/70">
                            <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#FFD400]" strokeWidth={3} />Licensed OR + WA</span>
                            <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#FFD400]" strokeWidth={3} />Fully insured</span>
                            <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#FFD400]" strokeWidth={3} />Free quotes</span>
                        </div>
                        <div className="mt-9 flex flex-col sm:flex-row gap-4">
                            <a href={SITE.phoneHref} data-testid="roof-call-cta">
                                <Button className="rounded-none h-14 px-7 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold tracking-wide text-base font-['Manrope']">
                                    <Phone className="w-5 h-5 mr-3" strokeWidth={2.5} />
                                    CALL {SITE.phoneDisplay}
                                </Button>
                            </a>
                            <a href="/#contact" data-testid="roof-quote-cta">
                                <Button variant="outline" className="rounded-none h-14 px-7 border-2 border-[#F6F5F2] bg-transparent text-[#F6F5F2] hover:bg-[#F6F5F2] hover:text-[#111111] font-bold tracking-wide text-base font-['Manrope']">
                                    FREE SAME-DAY QUOTE
                                    <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                                </Button>
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <div className="relative aspect-[4/5] w-full max-w-md mx-auto lg:max-w-none overflow-hidden border-2 border-[#F6F5F2]/20 shadow-block bg-[#111111]">
                            <BeforeAfterSlider
                                beforeSrc={IMG_HERO}
                                beforeAlt="BEFORE — moss-covered asphalt shingle roof in Southern Oregon before 5 Star Construction de-mossing treatment"
                                afterSrc={IMG_CLEAN}
                                afterAlt="AFTER — clean shingled residential roof after professional moss removal by 5 Star Construction"
                            />
                            <div className="absolute -top-4 -right-4 hidden md:flex w-32 h-32 bg-[#E11D2A] text-white items-center justify-center flex-col border-2 border-[#111111] shadow-block-sm -rotate-3 pointer-events-none z-10">
                                <div className="font-['Anton'] text-2xl tracking-tight uppercase leading-none">Same</div>
                                <div className="font-['Anton'] text-2xl tracking-tight uppercase leading-tight">Day</div>
                                <div className="font-['Manrope'] text-[9px] uppercase tracking-[0.18em] font-bold mt-1">Most homes</div>
                            </div>
                            <div className="absolute bottom-0 inset-x-0 bg-[#FFD400] text-[#111111] p-4 pointer-events-none z-10">
                                <div className="font-['Manrope'] text-[11px] uppercase tracking-[0.22em] font-bold">Drag the slider · see the transformation</div>
                                <div className="font-display text-base font-extrabold mt-0.5">Hand-scrape · soft-wash · never a power washer</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Premier callout strip */}
            <section className="bg-[#FFD400] border-b-4 border-[#111111]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-6 md:py-8 flex flex-col sm:flex-row items-center gap-4 justify-center text-center">
                    <div className="flex items-center gap-2">
                        {["a","b","c","d","e"].map((k) => (<Star key={`hd-star-${k}`} className="w-5 h-5 fill-[#111111] text-[#111111]" strokeWidth={0} />))}
                    </div>
                    <div className="font-['Anton'] tracking-tight uppercase text-[#111111] text-xl sm:text-2xl md:text-3xl">
                        Most Competitive Prices · <span className="text-[#E11D2A]">Same-Day Roof Transformations</span>
                    </div>
                </div>
            </section>

            {/* Why us */}
            <section className="bg-white border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="grid lg:grid-cols-12 gap-8 mb-12">
                        <div className="lg:col-span-5">
                            <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Why homeowners pick us</div>
                            <h2 className="mt-4 font-display font-extrabold tracking-tight text-[#111812] text-4xl sm:text-5xl">
                                Fair prices.
                                <br />
                                Fast finish.
                                <br />
                                Clean roof.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 lg:pt-3">
                            <p className="font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                                Roof moss is a PNW problem — wet winters, mild summers, tall trees. If your shingles look green, black, or fuzzy from the ground, moss is already lifting them and holding water underneath. We fix that in a day, without the franchise mark-up, and without ever putting a pressure washer to your shingles.
                            </p>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {WHY_US.map(({ i: I, t, d }) => (
                            <div key={t} className="bg-[#F6F5F2] border border-[#E2DFD8] hover:border-[#111111] hover:shadow-block-sm transition-all p-6" data-testid={`roof-why-${t.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
                                <div className="w-12 h-12 bg-[#E11D2A] text-white border border-[#111111] flex items-center justify-center mb-5">
                                    <I className="w-5 h-5" strokeWidth={2.25} />
                                </div>
                                <div className="font-display text-lg font-extrabold text-[#111812]">{t}</div>
                                <p className="mt-2 font-['Manrope'] text-sm text-[#5F5F5F] leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* What we do */}
            <section className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden border-2 border-[#111111] shadow-block bg-[#111111] max-w-md lg:max-w-none mx-auto">
                            <img src={IMG_MOSS_DETAIL} alt="Green moss growing on wavy roof tiles — before treatment" loading="lazy" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">What&apos;s included</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                            The whole roof-cleaning job — one price.
                        </h2>
                        <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                            No upsells, no line-item surprises. Every quote covers the full treatment plus the cleanup afterward.
                        </p>
                        <div className="mt-8 grid sm:grid-cols-2 gap-4">
                            {WHAT_WE_DO.map(({ i: I, t, d }) => (
                                <div key={t} className="bg-white border border-[#E2DFD8] p-5" data-testid={`roof-service-${t.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-9 h-9 bg-[#FFD400] border border-[#111111] flex items-center justify-center shrink-0">
                                            <I className="w-4 h-4 text-[#111111]" strokeWidth={2.25} />
                                        </div>
                                        <div className="font-display text-base font-extrabold text-[#111812]">{t}</div>
                                    </div>
                                    <p className="font-['Manrope'] text-sm text-[#5F5F5F] leading-relaxed">{d}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Process */}
            <section className="bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden border-2 border-[#F6F5F2]/20 shadow-block bg-[#111111] max-w-md lg:max-w-none mx-auto">
                            <img src={IMG_CLEAN} alt="Clean, moss-free residential roof after professional treatment — Southern Oregon and Portland Metro" loading="lazy" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#FFD400]">How the day goes</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl">
                            Assess. Protect. Remove. Clean up.
                        </h2>
                        <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#F6F5F2]/75 leading-relaxed max-w-2xl">
                            Same four steps, same crew, same day. Most homes go from moss-caked to spotless before you finish your afternoon coffee.
                        </p>
                        <ol className="mt-8 space-y-5">
                            {PROCESS.map((s, idx) => (
                                <li key={s.t} className="flex gap-5 items-start" data-testid={`roof-step-${idx}`}>
                                    <div className="shrink-0 w-12 h-12 bg-[#FFD400] text-[#111111] flex items-center justify-center font-['Anton'] text-xl">
                                        0{idx + 1}
                                    </div>
                                    <div>
                                        <div className="font-display text-lg md:text-xl font-extrabold text-[#F6F5F2]">{s.t}</div>
                                        <p className="font-['Manrope'] text-base text-[#F6F5F2]/70 mt-1 leading-relaxed">{s.d}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            {/* Service area */}
            <section className="bg-white border-b border-[#E2DFD8]" data-testid="roof-service-area">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="text-center mb-12 max-w-3xl mx-auto">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Where we work</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                            Medford to Portland to Vancouver &amp; Battle Ground WA.
                        </h2>
                        <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#5F5F5F]">
                            We service homeowners across Southern Oregon, the Portland Metro area, and Southwest Washington. If you&apos;re nearby, just call — we probably cover you.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                        {SERVICE_AREAS.map((a, idx) => (
                            <div key={a.region} className="bg-[#F6F5F2] border border-[#E2DFD8] hover:border-[#111111] hover:shadow-block-sm transition-all p-7" data-testid={`roof-area-${idx}`}>
                                <div className="w-11 h-11 bg-[#111111] text-[#FFD400] flex items-center justify-center mb-5">
                                    <MapPin className="w-5 h-5" strokeWidth={2.5} />
                                </div>
                                <div className="font-display text-lg font-extrabold text-[#111812]">{a.region}</div>
                                <p className="mt-2 font-['Manrope'] text-sm text-[#5F5F5F] leading-relaxed">{a.cities}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <div className="font-['Manrope'] text-sm text-[#5F5F5F]">Not sure if you&apos;re in our service area?</div>
                        <a href={SITE.phoneHref} data-testid="roof-area-cta">
                            <Button className="rounded-none h-11 px-5 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold tracking-wide font-['Manrope']">
                                <Phone className="w-4 h-4 mr-2" strokeWidth={2.5} />
                                CALL AND ASK
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
                <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="mb-12 max-w-3xl">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Frequently asked</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                            Roof de-mossing questions we hear every week.
                        </h2>
                    </div>
                    <div className="space-y-4">
                        {FAQ.map((it, i) => (
                            <details key={it.q} className="group bg-white border border-[#E2DFD8] hover:border-[#111111] transition-colors" data-testid={`roof-faq-${i}`}>
                                <summary className="cursor-pointer list-none p-5 md:p-6 flex items-start justify-between gap-4 font-display text-base md:text-lg font-extrabold text-[#111812]">
                                    <span>{it.q}</span>
                                    <span className="text-[#E11D2A] font-['Anton'] text-2xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                                </summary>
                                <div className="px-5 pb-5 md:px-6 md:pb-6 font-['Manrope'] text-base text-[#5F5F5F] leading-relaxed">{it.a}</div>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#FFD400] border-b-4 border-[#111111]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-14 md:py-16 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <h2 className="font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111111] text-4xl sm:text-5xl">
                            Same day. Fair price. <span className="bg-[#E11D2A] text-[#F6F5F2] px-3 py-0.5">Clean roof.</span>
                        </h2>
                        <p className="mt-4 font-['Manrope'] text-base md:text-lg text-[#111111]/85 max-w-2xl">
                            Free quote by phone or in-person. We&apos;ll match or beat any legit competitor. CCB #232477.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3">
                        <a href={SITE.phoneHref} className="block flex-1" data-testid="roof-cta-call">
                            <Button className="rounded-none w-full h-14 px-6 bg-[#111111] hover:bg-[#000000] text-white font-bold tracking-wide text-base font-['Manrope']">
                                <Phone className="w-5 h-5 mr-2" strokeWidth={2.5} />
                                CALL {SITE.phoneDisplay}
                            </Button>
                        </a>
                        <a href="/#contact" className="block flex-1" data-testid="roof-cta-form">
                            <Button variant="outline" className="rounded-none w-full h-14 px-6 border-2 border-[#111111] bg-transparent text-[#111111] hover:bg-[#111111] hover:text-white font-bold tracking-wide text-base font-['Manrope']">
                                REQUEST A QUOTE
                                <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* Attribution */}
            <section className="bg-[#111111] text-[#F6F5F2]/55">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-5 text-[11px] font-['Manrope'] leading-relaxed">
                    Illustrative before/after roof-moss reference photos. Real 5 Star Construction project photos added as jobs complete.
                </div>
            </section>

            <Footer />
        </div>
    );
}
